import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getNotes, deleteNote } from "../utils/api";
import Sidebar from "../components/Sidebar";
import { useAutoRefresh } from "../utils/useAutoRefresh";

const STATUS_TABS = [
  { value: "all", label: "All notes" },
  { value: "draft", label: "Drafts" },
  { value: "pending_signature", label: "Pending" },
  { value: "signed", label: "Signed" },
];

const PAGE_SIZES = [10, 15, 25];

function getPaginationItems(totalPages, currentPage) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage]);
  if (currentPage > 1) pages.add(currentPage - 1);
  if (currentPage < totalPages) pages.add(currentPage + 1);

  const sortedPages = [...pages].sort((a, b) => a - b);
  const items = [];
  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push(`ellipsis-${page}`);
    }
    items.push(page);
  });
  return items;
}

function Icon({ name, size = 16 }) {
  const paths = {
    plus: (
      <>
        <path d="M12 5v14M5 12h14" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    filter: (
      <>
        <path d="M4 6h16M7 12h10M10 18h4" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7v5h-5" />
        <path d="M19 12a7 7 0 1 0-2 5" />
      </>
    ),
    chevron: (
      <>
        <path d="m9 18 6-6-6-6" />
      </>
    ),
    trash: (
      <>
        <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
        <path d="M10 11v6M14 11v6" />
      </>
    ),
    file: (
      <>
        <path d="M7 3h7l4 4v14H7z" />
        <path d="M14 3v5h5M10 13h5M10 17h5" />
      </>
    ),
  };

  return (
    <svg
      className="ui-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("all");
  const [dateFilters, setDateFilters] = useState({ weekFrom: "", weekTo: "" });
  const [clientFilter, setClientFilter] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortCol, setSortCol] = useState("weekBegin");
  const [sortDir, setSortDir] = useState("desc");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 900,
  );

  useEffect(() => {
    const handleResize = () => setSidebarCollapsed(window.innerWidth < 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchNotes = async () => {
    try {
      const data = await getNotes();
      setNotes(data.notes || []);
      setLastUpdated(new Date());
    } catch (err) {
      showToast(err.message || "Could not load notes", "error");
    } finally {
      setLoading(false);
    }
  };

  useAutoRefresh(fetchNotes);

  const handleDelete = async (id, clientName) => {
    if (!window.confirm(`Delete notes for ${clientName || "this client"}?`))
      return;
    try {
      await deleteNote(id);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      showToast("Note deleted", "success");
    } catch {
      showToast("Could not delete note", "error");
    }
  };

  const handleSort = (col) => {
    setCurrentPage(1);
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const formatDate = (str) => {
    if (!str) return "Not available";
    return new Date(str + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatus = (note) => note.status || "draft";

  const statusBadge = (note) => {
    const status = getStatus(note);
    const labels = {
      draft: "Draft",
      pending_signature: "Pending signature",
      signed: "Signed",
    };
    return (
      <span className={`enterprise-status enterprise-status--${status}`}>
        <span className="enterprise-status-dot" />
        {labels[status] || status}
      </span>
    );
  };

  const counts = notes.reduce(
    (acc, note) => {
      const status = getStatus(note);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { draft: 0, pending_signature: 0, signed: 0 },
  );

  const now = Date.now();
  const thisWeekCount = notes.filter((note) => {
    if (!note.weekBegin) return false;
    const age =
      (now - new Date(note.weekBegin + "T00:00:00").getTime()) / 86400000;
    return age >= 0 && age < 7;
  }).length;

  const hasDateFilters = dateFilters.weekFrom || dateFilters.weekTo;
  const hasAdvancedFilters = hasDateFilters || clientFilter;
  const advancedFilterCount =
    [dateFilters.weekFrom, dateFilters.weekTo].filter(Boolean).length +
    (clientFilter ? 1 : 0);
  const clientOptions = [
    ...new Set(notes.map((note) => note.clientName).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
  const clearFilters = () => {
    setSearch("");
    setActiveStatus("all");
    setDateFilters({ weekFrom: "", weekTo: "" });
    setClientFilter("");
    setCurrentPage(1);
  };

  const filteredNotes = notes.filter((note) => {
    const query = search.trim().toLowerCase();
    if (query) {
      const searchable = [
        note.clientName,
        note.caregiverName,
        note.assignedToName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!searchable.includes(query)) return false;
    }
    if (activeStatus !== "all" && getStatus(note) !== activeStatus)
      return false;
    if (clientFilter && note.clientName !== clientFilter) return false;
    if (
      dateFilters.weekFrom &&
      note.weekBegin &&
      note.weekBegin < dateFilters.weekFrom
    )
      return false;
    if (
      dateFilters.weekTo &&
      note.weekBegin &&
      note.weekBegin > dateFilters.weekTo
    )
      return false;
    return true;
  });

  const sortedNotes = [...filteredNotes].sort((a, b) => {
    let av = "";
    let bv = "";
    if (sortCol === "clientName") {
      av = a.clientName || "";
      bv = b.clientName || "";
    } else if (sortCol === "weekBegin") {
      av = a.weekBegin || "";
      bv = b.weekBegin || "";
    } else if (sortCol === "savedAt") {
      av = a.savedAt || "";
      bv = b.savedAt || "";
    } else if (sortCol === "status") {
      av = getStatus(a);
      bv = getStatus(b);
    }
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(sortedNotes.length / pageSize));
  const pageStart = sortedNotes.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, sortedNotes.length);
  const paginatedNotes = sortedNotes.slice(pageStart, pageEnd);
  const paginationItems = getPaginationItems(totalPages, currentPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    activeStatus,
    dateFilters.weekFrom,
    dateFilters.weekTo,
    pageSize,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const tabCount = (value) =>
    value === "all" ? notes.length : counts[value] || 0;

  const SortArrow = ({ col }) => (
    <span
      className={`sort-arrow${sortCol === col ? " sort-arrow--active" : ""}`}
    >
      {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  return (
    <div className="app-shell-with-sidebar enterprise-shell">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
      />

      <main className="main-content enterprise-main">
        <header className="enterprise-header">
          <div className="enterprise-heading">
            <h1>Progress Notes</h1>
            <p>Welcome back, {user?.firstName}.</p>
          </div>
          <div className="enterprise-header-actions">
            <button
              className="enterprise-icon-button"
              type="button"
              onClick={fetchNotes}
              title="Refresh notes"
              aria-label="Refresh notes"
            >
              <Icon name="refresh" />
            </button>
            <Link to="/notes/new" className="btn enterprise-primary-button">
              <Icon name="plus" />
              New progress note
            </Link>
          </div>
        </header>

        <section
          className="enterprise-summary"
          aria-label="Progress note summary"
        >
          <div className="enterprise-summary-item">
            <span className="enterprise-summary-value">{notes.length}</span>
            <span className="enterprise-summary-label">Total notes</span>
          </div>
          <div className="enterprise-summary-divider" />
          <div className="enterprise-summary-item">
            <span className="enterprise-summary-value">{thisWeekCount}</span>
            <span className="enterprise-summary-label">This week</span>
          </div>
          <div className="enterprise-summary-divider" />
          <div className="enterprise-summary-item enterprise-summary-item--attention">
            <span className="enterprise-summary-value">
              {counts.pending_signature}
            </span>
            <span className="enterprise-summary-label">Awaiting signature</span>
          </div>
          <div className="enterprise-summary-divider" />
          <div className="enterprise-summary-item enterprise-summary-item--success">
            <span className="enterprise-summary-value">{counts.signed}</span>
            <span className="enterprise-summary-label">Completed</span>
          </div>
          <div className="enterprise-summary-updated">
            <span className="enterprise-live-dot" />
            {lastUpdated
              ? `Updated ${lastUpdated.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
              : "Syncing notes"}
          </div>
        </section>

        <section className="enterprise-notes-panel">
          <div className="enterprise-panel-toolbar">
            <div className="enterprise-panel-title">
              <h2>All progress notes</h2>
              <span>
                {filteredNotes.length}{" "}
                {filteredNotes.length === 1 ? "record" : "records"}
              </span>
            </div>
            <div className="enterprise-toolbar-actions">
              <label className="enterprise-search">
                <Icon name="search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search client or caregiver"
                  aria-label="Search notes"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </label>
              <button
                type="button"
                className={`enterprise-filter-button${filtersOpen || hasAdvancedFilters ? " enterprise-filter-button--active" : ""}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
              >
                <Icon name="filter" />
                Filters
                {hasAdvancedFilters && (
                  <span className="enterprise-filter-count">
                    {advancedFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div
            className="enterprise-status-tabs"
            role="tablist"
            aria-label="Filter notes by status"
          >
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={activeStatus === tab.value}
                className={activeStatus === tab.value ? "active" : ""}
                onClick={() => setActiveStatus(tab.value)}
              >
                {tab.label}
                <span>{tabCount(tab.value)}</span>
              </button>
            ))}
          </div>

          {filtersOpen && (
            <div className="enterprise-advanced-filters">
              <div className="enterprise-date-field">
                <label htmlFor="client-filter">Client</label>
                <select
                  id="client-filter"
                  value={clientFilter}
                  onChange={(event) => setClientFilter(event.target.value)}
                >
                  <option value="">All clients</option>
                  {clientOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="enterprise-date-field">
                <label htmlFor="week-from">Week beginning from</label>
                <input
                  id="week-from"
                  type="date"
                  value={dateFilters.weekFrom}
                  onChange={(event) =>
                    setDateFilters((filters) => ({
                      ...filters,
                      weekFrom: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="enterprise-date-field">
                <label htmlFor="week-to">Week beginning to</label>
                <input
                  id="week-to"
                  type="date"
                  value={dateFilters.weekTo}
                  onChange={(event) =>
                    setDateFilters((filters) => ({
                      ...filters,
                      weekTo: event.target.value,
                    }))
                  }
                />
              </div>
              {(search || activeStatus !== "all" || hasAdvancedFilters) && (
                <button
                  type="button"
                  className="enterprise-clear-button"
                  onClick={clearFilters}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          <div className="enterprise-table-region">
            {loading ? (
              <div className="enterprise-loading-state">
                <div className="spinner" />
                <span>Loading progress notes</span>
              </div>
            ) : notes.length === 0 ? (
              <div className="enterprise-empty-state">
                <div className="enterprise-empty-icon">
                  <Icon name="file" size={24} />
                </div>
                <h3>No progress notes yet</h3>
                <p>Create the first weekly progress note for your client.</p>
                <Link to="/notes/new" className="btn enterprise-primary-button">
                  <Icon name="plus" /> Create progress note
                </Link>
              </div>
            ) : sortedNotes.length === 0 ? (
              <div className="enterprise-empty-state">
                <div className="enterprise-empty-icon">
                  <Icon name="search" size={24} />
                </div>
                <h3>No matching notes</h3>
                <p>Adjust your search, status, or date filters.</p>
                <button
                  type="button"
                  className="enterprise-clear-button"
                  onClick={clearFilters}
                >
                  Clear all filters
                </button>
              </div>
            ) : (
              <>
                <div className="notes-table-wrap enterprise-table-scroll">
                  <table className="notes-table-new enterprise-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort("clientName")}>
                          Client <SortArrow col="clientName" />
                        </th>
                        <th onClick={() => handleSort("weekBegin")}>
                          Week beginning <SortArrow col="weekBegin" />
                        </th>
                        <th onClick={() => handleSort("status")}>
                          Status <SortArrow col="status" />
                        </th>
                        <th onClick={() => handleSort("savedAt")}>
                          Last saved <SortArrow col="savedAt" />
                        </th>
                        <th className="enterprise-actions-heading">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedNotes.map((note) => (
                        <tr key={note.id}>
                          <td>
                            <Link
                              to={`/notes/${note.id}`}
                              className="enterprise-client-cell"
                            >
                              <span className="enterprise-client-avatar">
                                {(note.clientName || "?")
                                  .trim()
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                              <span>
                                <strong>
                                  {note.clientName || "Client name unavailable"}
                                </strong>
                              </span>
                            </Link>
                          </td>
                          <td>
                            <span className="enterprise-date-primary">
                              {formatDate(note.weekBegin)}
                            </span>
                          </td>
                          <td>{statusBadge(note)}</td>
                          <td>
                            <span className="enterprise-date-primary">
                              {note.savedAt
                                ? new Date(note.savedAt).toLocaleDateString(
                                    "en-US",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    },
                                  )
                                : "Not saved"}
                            </span>
                            {note.savedAt && (
                              <span className="enterprise-date-secondary">
                                {new Date(note.savedAt).toLocaleTimeString(
                                  "en-US",
                                  { hour: "numeric", minute: "2-digit" },
                                )}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="enterprise-row-actions">
                              <Link
                                to={`/notes/${note.id}`}
                                className="enterprise-open-link"
                              >
                                Open <Icon name="chevron" size={14} />
                              </Link>
                              {user?.isAdmin && (
                                <button
                                  type="button"
                                  className="enterprise-delete-btn"
                                  onClick={() =>
                                    handleDelete(note.id, note.clientName)
                                  }
                                  aria-label={`Delete note for ${note.clientName || "this client"}`}
                                  title="Delete note"
                                >
                                  <Icon name="trash" size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mobile-notes-list enterprise-mobile-list">
                  {paginatedNotes.map((note) => (
                    <article key={note.id} className="enterprise-mobile-card">
                      <div className="enterprise-mobile-card-top">
                        <Link
                          to={`/notes/${note.id}`}
                          className="enterprise-client-cell"
                        >
                          <span className="enterprise-client-avatar">
                            {(note.clientName || "?")
                              .trim()
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                          <span>
                            <strong>
                              {note.clientName || "Client name unavailable"}
                            </strong>
                            <small>Week of {formatDate(note.weekBegin)}</small>
                          </span>
                        </Link>
                        {statusBadge(note)}
                      </div>
                      <div className="enterprise-mobile-card-bottom">
                        <span>
                          {note.savedAt
                            ? `Saved ${new Date(note.savedAt).toLocaleDateString()}`
                            : "Not saved"}
                        </span>
                        <div className="enterprise-mobile-card-actions">
                          {user?.isAdmin && (
                            <button
                              type="button"
                              className="enterprise-delete-btn"
                              onClick={() =>
                                handleDelete(note.id, note.clientName)
                              }
                              aria-label={`Delete note for ${note.clientName || "this client"}`}
                            >
                              <Icon name="trash" size={14} />
                            </button>
                          )}
                          <Link
                            to={`/notes/${note.id}`}
                            className="enterprise-open-link"
                          >
                            Open note <Icon name="chevron" size={14} />
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>

          {!loading && sortedNotes.length > 0 && (
            <footer className="enterprise-pagination">
              <label className="enterprise-page-size">
                <span>Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                  aria-label="Rows per page"
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>

              <div className="enterprise-page-range" aria-live="polite">
                Showing {pageStart + 1}–{pageEnd} of {sortedNotes.length}
              </div>

              <nav
                className="enterprise-page-controls"
                aria-label="Progress notes pagination"
              >
                <button
                  type="button"
                  className="enterprise-page-arrow"
                  onClick={() => setCurrentPage((page) => page - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {paginationItems.map((item) =>
                  typeof item === "number" ? (
                    <button
                      key={item}
                      type="button"
                      className={`enterprise-page-number${currentPage === item ? " active" : ""}`}
                      onClick={() => setCurrentPage(item)}
                      aria-label={`Page ${item}`}
                      aria-current={currentPage === item ? "page" : undefined}
                    >
                      {item}
                    </button>
                  ) : (
                    <span key={item} className="enterprise-page-ellipsis">
                      …
                    </span>
                  ),
                )}
                <button
                  type="button"
                  className="enterprise-page-arrow"
                  onClick={() => setCurrentPage((page) => page + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  ›
                </button>
              </nav>
            </footer>
          )}
        </section>
      </main>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
